import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useHistory, useParams } from 'react-router-dom';
import { USBButton } from '@usb-shield/forms-beta';
import {
    Header,
    Footer,
} from '@usb-onboarding/product-recommender-components';
import RelationshipDetail from '../../molecules/RelationshipDetail';
import PageLevelNotification from '../../molecules/PageLevelNotification';
import styles from './bankerQuestionnaire.scss';
import '@usb-shield/react-notification/dist/library/styles/index.css';
import '@usb-shield/react-forms-base/dist/library/styles/index.css';
import {
    sendBPSQuestion,
    backToLastQuestion,
    resetSessionID,
    updateBusinessType,
    updateAppPageAnswers,
    setBackButton,
    resetReviewQuestions,
    updateUnsavedAnswersOnSave,
    clearUnsavedAnswersOnDiscard,
} from '../../redux/actions';
import {
    resetSelection,
    updateVisibleQuestions,
} from '../../redux/actions/app';
import {
    getActiveID,
    getQuestionaire,
    getFormErrors,
    getErrorMessage,
    dynamicQuestionErrorCount,
    getElementAnswer,
    updateVisibleQuestionUtils,
    resetCorrelationId,
    filterInvalidQuestions,
    getDPSGeneralReportingData,
    dispatchReportingEvent,
    tealiumUpdatedPageView,
    getCurrentPageTitleForReportingData,
    getVisibleQuestions,
    constructAnswerResponse,
} from '../../utils';
import { productConfiguration } from '../../config';
import {
    firstQuestionPath,
    productBPSID,
    timeoutRedirectTo,
} from '../../constants';
import Loader from '../../atoms/Loader';
import defaultLang from '../../language';
import QuestionGroup from '../../molecules/QuestionGroup/QuestionGroup';
import { formUpdateThunk } from '../../redux/middleware/formUpdates';
import { PAGE_ACTION_BACK } from '../../constants/dynamicLanding';

function BankerQuestionnaire() {
    const [locationKeys, setLocationKeys] = useState([]);
    const [errorNotifications, setErrorNotifications] = useState([]);
    const history = useHistory();
    const dispatch = useDispatch();
    const { questionID } = useParams();
    const error = useSelector((state) => state.errorReducer.error);
    const AEMContent = useSelector(
        (state) => state.AEMContentReducer.AEMContent,
    );
    const backButtonFlag = useSelector(
        (state) => state.dynamicLandingReducer.backButtonFlag,
    );
    const sessionID = useSelector(
        (state) => state.dynamicLandingReducer.sessionID,
    );
    const pageAction = useSelector(
        (state) => state.dynamicLandingReducer.pageAction,
    );
    const activeQuestionID = useSelector(
        (state) => state.dynamicLandingReducer.activeQuestionID,
    );
    const questionnaireState = useSelector(
        (state) => state.dynamicLandingReducer.questionnaireState,
    );
    const allQuestions = useSelector(
        (state) => state.dynamicLandingReducer.allQuestionaire,
    );
    const visibleQuestions = useSelector(
        (state) => state.appReducer.visibleQuestions,
    );
    const unsavedAnswerContainer = useSelector(
        (state) => state.appReducer.unsavedAnswerContainer,
    );
    const relationshipName = useSelector(
        (state) => state.dynamicLandingReducer.relationshipName,
    );
    const selectedFieldValues = useSelector(
        (state) => state.dynamicLandingReducer.businessTypeState,
    );

    const dataArchive = useSelector(
        (state) => state.dynamicLandingReducer.dataArchive,
    );
    const answerContainer = useSelector(
        (state) => state.appReducer.answerContainer,
    );
    const questionNumber = questionID
        ? questionID.substr(1, 1)
        : null;
    const isLoading = useSelector(
        (state) => state.loaderReducer.isLoading,
    );
    const reviewQuestions = useSelector(
        (state) => state.appReducer.reviewQuestions,
    );
    const [dynamicErrorCount, setDynamicErrorCount] = useState(
        dynamicQuestionErrorCount(allQuestions),
    );
    // TODO: restore setFormData
    // const [formData, setFormData] = useState(
    //     dynamicQuestionValidation(allQuestions),
    // );
    const previousShieldSelections = useSelector(
        (state) => state.questionnaireReducer.currentShieldData,
    );
    const updateSiteCatAndTelium = (id) => {
        const currentPage = getCurrentPageTitleForReportingData(id);
        const answerResponse =
            constructAnswerResponse(answerContainer);
        const reportingData = getDPSGeneralReportingData(
            currentPage,
            activeQuestionID === firstQuestionPath
                ? defaultLang.siteCat.formState.start
                : null,
            answerResponse,
        );

        dispatchReportingEvent(reportingData);
        tealiumUpdatedPageView();
        window.scrollTo(0, 0);
    };
    useEffect(() => {
        if (activeQuestionID) {
            const visibleQuestionsForQuestionnaire =
                getVisibleQuestions(
                    JSON.parse(JSON.stringify(answerContainer)),
                    activeQuestionID,
                );
            dispatch(
                updateVisibleQuestions(
                    visibleQuestionsForQuestionnaire,
                ),
            );
        }
    }, [activeQuestionID]);
    useEffect(() => {
        // reset the form data every time the active question ID changes
        // setFormData(dynamicQuestionValidation(allQuestions));
        setDynamicErrorCount(dynamicQuestionErrorCount(allQuestions));
        setErrorNotifications([]);
        if (activeQuestionID) {
            updateSiteCatAndTelium(activeQuestionID);
        }
    }, [activeQuestionID]);

    const handleOnSubmit = () => {
        const [notifications, dynamicFieldUpdate] = getFormErrors(
            dynamicErrorCount,
            visibleQuestions,
            activeQuestionID,
            styles,
            allQuestions,
            unsavedAnswerContainer,
        );
        if (notifications.length > 0) {
            setErrorNotifications(notifications);
            setDynamicErrorCount(dynamicFieldUpdate);
        } else {
            dispatch(updateUnsavedAnswersOnSave());
            const allValue = unsavedAnswerContainer[activeQuestionID];
            const selectedQuestionnaire =
                allValue &&
                Object.keys(allValue).map((val) => {
                    const ans = allValue[val]?.map((filterval) => ({
                        id: filterval?.value || filterval,
                    }));
                    return { questionId: val, answers: ans };
                });
            const payload = {
                productID: productConfiguration[productBPSID],
                sessionID,
                questionID,
                customerID: sessionID,
                answerInfo: allValue,
                selectedQuestionnaire: filterInvalidQuestions(
                    selectedQuestionnaire,
                ),
                unsavedAnswerContainer,
            };
            dispatch(sendBPSQuestion(payload));
        }
    };
    const universalUpdateCallback =
        (universalQuestionID) => (shieldReturnValue) => {
            const inputValue =
                shieldReturnValue.inputValue !== undefined
                    ? shieldReturnValue.inputValue
                    : shieldReturnValue;
            const nestedQuestionList =
                allQuestions?.[0]?.metaData.nestedQuestionLinks;
            const selectedFieldValLocal = {
                ...selectedFieldValues,
                [universalQuestionID]: inputValue,
            };
            const allValue = unsavedAnswerContainer[activeQuestionID];
            updateVisibleQuestionUtils(
                inputValue,
                selectedFieldValLocal,
                nestedQuestionList,
                allQuestions,
                visibleQuestions,
                allValue,
                reviewQuestions || backButtonFlag,
            );
            dispatch(updateBusinessType(selectedFieldValLocal));
            dispatch(
                formUpdateThunk({
                    universalQuestionID,
                    shieldReturnValue,
                    previousShieldSelections,
                    visibleQuestions,
                    activeQuestionID,
                    unsavedAnswerContainer,
                }),
            );
            dispatch(
                updateAppPageAnswers(
                    universalQuestionID,
                    visibleQuestions,
                    shieldReturnValue,
                    unsavedAnswerContainer,
                ),
            );
        };
    const constructedQuestionForm = visibleQuestions.map(
        (visibleID) => {
            const thisQuestion = allQuestions?.filter(
                (aQuestion) => aQuestion.id === visibleID,
            )?.[0];
            let selectedValue = '';
            // if (pageAction === PAGE_ACTION_BACK) {
            selectedValue = getElementAnswer(
                unsavedAnswerContainer,
                activeQuestionID,
                thisQuestion,
            );
            // }
            const updatedAnswers = [];
            const pagesList = Object.keys(dataArchive);
            const lastid = pagesList[pagesList.length - 1];

            if (Array.isArray(selectedValue)) {
                thisQuestion?.answers?.forEach((item) => {
                    const newItem = item;
                    if (
                        selectedValue.includes(item.id) &&
                        !(
                            lastid === activeQuestionID &&
                            backButtonFlag
                        )
                    ) {
                        newItem.checked = true;
                    } else {
                        newItem.checked = false;
                    }
                    updatedAnswers.push(newItem);
                });
            }
            if (
                pageAction === PAGE_ACTION_BACK &&
                updatedAnswers &&
                updatedAnswers.length > 0
            ) {
                thisQuestion.answers = updatedAnswers;
            }
            const errorMessage = getErrorMessage(thisQuestion);
            if (thisQuestion)
                return (
                    <>
                        <QuestionGroup
                            key={thisQuestion.id}
                            fullQuestion={thisQuestion}
                            questionCallback={universalUpdateCallback(
                                thisQuestion.id,
                            )}
                            inputValue={selectedValue}
                            dynamicErrorCount={
                                dynamicErrorCount[visibleID]
                            }
                            errorMessages={errorMessage}
                        />
                    </>
                );
            return null;
        },
    );
    function goBack() {
        dispatch(clearUnsavedAnswersOnDiscard());
        if (questionNumber > 1) {
            const pagesList = Object.keys(dataArchive);
            const { getPreviousPageIds } = getActiveID(
                activeQuestionID,
                pagesList,
                questionnaireState,
            );
            const nextQuestion = getQuestionaire(
                getPreviousPageIds,
                dataArchive,
            );
            dispatch(
                backToLastQuestion(getPreviousPageIds, nextQuestion),
            );
            dispatch(setBackButton(true));
        } else {
            dispatch(setBackButton(false));
            if (!reviewQuestions) {
                dispatch(resetSelection());
            }
            dispatch(resetSessionID());
            resetCorrelationId();
            history.push(`/banker/start`);
        }
    }

    if (productConfiguration[productBPSID]) {
        useEffect(
            () =>
                history.listen((location) => {
                    if (history.action === 'PUSH') {
                        setLocationKeys([location.key]);
                    }

                    if (history.action === 'POP') {
                        setLocationKeys((keys) => [
                            location.key,
                            ...keys,
                        ]);
                        goBack();
                    }
                }),
            [locationKeys],
        );

        useEffect(() => {
            if (activeQuestionID) {
                window.scrollTo(0, 0);
                const nextPath = `/banker/questionnaire/${activeQuestionID}`;
                history.push(nextPath);
            } else if (!activeQuestionID) {
                if (
                    sessionID &&
                    !window.location.pathname.includes('banker/start')
                ) {
                    history.push(`/banker/we-have-great-options`);
                    dispatch(resetReviewQuestions());
                }
            }
        }, [activeQuestionID]);
        const legendText = allQuestions?.[0]?.metaData?.question;
        const helperText = allQuestions?.[0]?.metaData?.helperText;

        useEffect(() => {
            if (!window.location.pathname.includes('banker/start')) {
                let redirectTo = '';
                if (!sessionID) {
                    redirectTo = timeoutRedirectTo;
                } else if (error) {
                    redirectTo = '/banker/error';
                    resetCorrelationId();
                }
                if (redirectTo) {
                    history.push(redirectTo);
                }
            }
        }, [error, sessionID]);
        const LAST_QUESTION_INDEX = 3;

        const nextSubmitButtonText =
            Object.keys(dataArchive)[LAST_QUESTION_INDEX] ===
            activeQuestionID
                ? 'Submit'
                : 'Next';

        return (
            <>
                {isLoading && <Loader />}
                <div className="banker-container-header">
                    <Header title={defaultLang.header.fullName} />
                    {questionNumber && (
                        <Helmet>
                            <title>
                                {`${defaultLang.pageToTitleMap[activeQuestionID]}`}{' '}
                                {' | '} Business Product Selector |
                                U.S. Bank
                            </title>
                        </Helmet>
                    )}
                </div>
                <main
                    id="app__content"
                    className="main-content-wrapper"
                >
                    <div className="main-left-content">
                        <PageLevelNotification
                            errorNotifications={errorNotifications}
                            handleErrorClick={() => {}}
                        />
                        <h1>
                            <legend>{legendText}</legend>
                        </h1>
                        <p>{helperText}</p>
                        <form>
                            <div className="question-wrapper">
                                {[...constructedQuestionForm]}
                            </div>
                            <div className="button-container">
                                <USBButton
                                    variant="text"
                                    ariaLabel="Return to previous page"
                                    onSubmit={goBack}
                                >
                                    Back
                                </USBButton>

                                <USBButton
                                    variant="primary"
                                    ariaLabel="Start questionnaire"
                                    type="submit"
                                    onSubmit={handleOnSubmit}
                                >
                                    {nextSubmitButtonText}
                                </USBButton>
                            </div>
                        </form>
                    </div>
                    <div className="main-right-content">
                        <RelationshipDetail name={relationshipName} />
                    </div>
                </main>
                <div className="banker-container-footer">
                    <Footer
                        header={defaultLang.footer.connectionTitle}
                        content={defaultLang.footer.footerDisclosure}
                        privacyLabel={AEMContent.footer.privacyLabel}
                        privacyUrl={AEMContent.footer.privacyUrl}
                        fdic={defaultLang.footer.connectionContent}
                        cobrowseLabel={
                            AEMContent.footer.cobrowseLabel
                        }
                        cobrowseUrl={AEMContent.footer.cobrowseUrl}
                    />
                </div>
            </>
        );
    }
}

export default BankerQuestionnaire;
